/**
 * El MATERIAL de una pieza IFC cuando el modelo no lo dice (ni `IfcMaterial.Category`, ni densidad, ni un
 * perfil de acero): lo reconoce la IA por el NOMBRE del material («Metal - Steel - 345 MPa», «Holz»), en
 * UNA consulta por modelo con la lista de nombres distintos (ver la ruta `materiales-ifc` de planos). Aquí
 * no hay ninguna lista de palabras: este archivo solo fija el VOCABULARIO de la respuesta, cómo se
 * normaliza un nombre para la caché y los textos que ve la persona.
 *
 * Lo que se pesa así queda marcado como ESTIMADO y con su motivo (la persona ve que el plano no lo traía y
 * que se dedujo). «otro» y «desconocido» no se pesan: no se inventa una densidad.
 */
import type { FamiliaMaterialIfc } from './parametrosMedicionIfc.js';

/** Lo que la IA puede contestar por cada nombre. «desconocido» es una respuesta válida. */
export const TIPOS_DE_MATERIAL_IFC = [
  'acero',
  'concreto',
  'concreto_ligero',
  'madera',
  'aluminio',
  'vidrio',
  'mamposteria',
  'yeso',
  'otro',
  'desconocido',
] as const;
export type TipoMaterialIfc = (typeof TIPOS_DE_MATERIAL_IFC)[number];

/** Cómo se nombra cada tipo en pantalla. */
export const NOMBRE_DE_TIPO_DE_MATERIAL: Readonly<Record<TipoMaterialIfc, string>> = {
  acero: 'acero',
  concreto: 'concreto',
  concreto_ligero: 'concreto ligero',
  madera: 'madera',
  aluminio: 'aluminio',
  vidrio: 'vidrio',
  mamposteria: 'mampostería',
  yeso: 'yeso',
  otro: 'otro material',
  desconocido: 'sin reconocer',
};

/**
 * Qué es cada tipo, tal como se le explica a la IA (el prompt se arma con esto, en este orden). Es el
 * VOCABULARIO: si cambia la definición de un tipo, lo que se guardó en caché con ese tipo deja de valer
 * solo (ver `huellaDeTipoDeMaterial`).
 */
export const DEFINICION_DE_TIPO_DE_MATERIAL: Readonly<Record<TipoMaterialIfc, string>> = {
  acero: 'acero estructural, lámina o perfil de acero, acero inoxidable.',
  concreto: 'concreto simple o armado de peso normal, mortero, concreto prefabricado.',
  concreto_ligero:
    'concreto ligero o aligerado: con agregado ligero, celular, espumado o aireado (colado o en paneles; ' +
    'un block de concreto ligero es mamposteria).',
  madera: 'madera maciza, laminada, contrachapada.',
  aluminio: 'aluminio.',
  vidrio: 'cristal, vidrio.',
  mamposteria: 'block, tabique, ladrillo, muro de piezas.',
  yeso: 'tablaroca, panel de yeso, aplanado de yeso.',
  otro:
    'el nombre sí dice qué es, pero no es ninguno de los anteriores (plástico, aislante, tierra, pintura, ' +
    'pieza de instalación…).',
  desconocido:
    'el nombre no dice de qué material es (un nombre genérico, un número, un color, un código sin ' +
    'significado). Si dudas, desconocido: NO adivines.',
};

/** FNV-1a de 32 bits, en hex: huella corta y determinista (no es criptográfica ni necesita serlo). */
function fnv1a(s: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h.toString(16).padStart(8, '0');
}

/**
 * La HUELLA de un tipo en el vocabulario vigente: lo que se guarda en la caché junto con la respuesta. Una
 * respuesta guardada vale mientras la huella de su tipo no cambie; así, al agregar un tipo o redefinir uno,
 * se vuelven a preguntar SOLO los nombres cuya respuesta podría cambiar:
 *  - la del tipo redefinido (al nacer «concreto ligero», «concreto» pasó a ser «de peso normal»);
 *  - la de «otro», que es «ninguno de los anteriores»: su huella incluye la lista entera, así que cambia
 *    cada vez que el vocabulario crece.
 * Los demás tipos («acero», «madera»…) no cambian de huella y su caché sigue valiendo.
 */
export function huellaDeTipoDeMaterial(t: TipoMaterialIfc): string {
  const base = `${t}: ${DEFINICION_DE_TIPO_DE_MATERIAL[t]}`;
  if (t !== 'otro') return fnv1a(base);
  const anteriores = TIPOS_DE_MATERIAL_IFC.filter((x) => x !== 'otro' && x !== 'desconocido')
    .map((x) => `${x}: ${DEFINICION_DE_TIPO_DE_MATERIAL[x]}`)
    .join(' | ');
  return fnv1a(`${base} | ${anteriores}`);
}

/** Huella vigente de cada tipo (para que la BD compare de una vez). */
export function huellasDelVocabularioDeMaterial(): Record<TipoMaterialIfc, string> {
  return Object.fromEntries(TIPOS_DE_MATERIAL_IFC.map((t) => [t, huellaDeTipoDeMaterial(t)])) as Record<TipoMaterialIfc, string>;
}

/** La familia con densidad en la calibración; «otro» / «desconocido» → null (no se pesa). */
export function familiaDeTipoDeMaterial(t: TipoMaterialIfc): FamiliaMaterialIfc | null {
  return t === 'otro' || t === 'desconocido' ? null : t;
}

/** Nombre normalizado de un material: la clave de la caché (mayúsculas, espacios y acentos compuestos). */
export function normalizarNombreDeMaterial(nombre: string): string {
  return nombre.normalize('NFC').replace(/\s+/g, ' ').trim().toLowerCase();
}

/** Tope de nombres por consulta (técnico: un modelo real trae 5–30). */
export const MAX_MATERIALES_A_CLASIFICAR = 200;
/** Tope de largo de un nombre que se manda. */
export const MAX_LARGO_NOMBRE_MATERIAL = 160;

/** Lo que el visor pide: los nombres de material que el modelo no tipifica (distintos). */
export interface ClasificarMaterialesIfcRequest {
  nombres: string[];
}

/** Lo que contesta la API: el tipo de cada nombre que se pudo clasificar (de la caché o de la IA). */
export interface ClasificarMaterialesIfcResponse {
  materiales: { nombre: string; tipo: TipoMaterialIfc }[];
  /** Texto único de IA caída (`MENSAJE_IA_NO_RESPONDE`) si faltó clasificar algo. null = todo bien. */
  avisoIa: string | null;
}

/** Un material del modelo que se reconoció por su nombre, para «Lo que el plano no traía y se dedujo». */
export interface MaterialDeducidoIfc {
  /** El nombre tal como lo trae el modelo. */
  nombre: string;
  tipo: TipoMaterialIfc;
  /** Piezas del inventario con ese material y sin tipo declarado. */
  piezas: number;
  /** De ellas, cuántas se pesaron con la densidad del tipo. */
  pesadas: number;
  /** expressID de esas piezas (una por pieza), para ubicarlas en el visor 3D. Solo es vista: no entra a ninguna cantidad. */
  expressIds?: number[];
}

/** El motivo del peso estimado de una pieza, para su ficha. */
export function motivoDelPesoDeducido(nombre: string, tipo: TipoMaterialIfc): string {
  return `Peso estimado: el modelo no dice de qué material es; por su nombre («${nombre}») se reconoció como ${NOMBRE_DE_TIPO_DE_MATERIAL[tipo]}.`;
}
